import { CookieService } from 'ngx-unificator/services';
import { delay, distinctUntilChanged, filter, map, switchMap, takeWhile, tap } from 'rxjs/operators';
import { PlatformService } from '../../core/services/platform.service';
import { StaticContentService } from '../../core/services/static-content.service';
import { UserService } from '../../core/services/user/user.service';
import { UserGroupsService } from '../../core/services/user/user-groups.service';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { inject } from '@angular/core';
import { ModalService } from '../../core/modal/modal.service';
import { combineLatest, Observable, of } from 'rxjs';
import { UserBonusesService } from '../../core/services/user/user-bonuses.service';
import { UserPaymentModeService } from '../../core/services/user/user-payment-mode.service';
import { StaticPageService } from '../static-page/static-page.service';
import { LootboxService } from '../../core/services/lootbox/lootbox.service';
import { TimeService } from '../../core/services/time.service';
import { TournamentsService } from '../tournaments/tournaments.service';
import { GlobalEventsService } from '../../core/services/global-events.service';
import { LocalHistoryService } from '../../core/services/local-history.service';
import { TranslationService } from '../../core/services/translation/translation.service';
import { WindowService } from '../../core/services/window.service';
import { LocalstorageService } from '../../core/services/localstorage.service';
import { WINDOW } from '@ng-web-apis/common';
import { HttpStatus, ResponseHandlerService } from '../../core/services/response-handler.service';
import { BonusStage } from '../../core/services/user/data/user-bonuses.data';
import { StoreEntityType } from 'ngx-unificator/store';

type StringEnum = Record<string, string>;

type ActionRouteConfig = {
  key: string;
  url: string;
  authUrl?: string[];
  nativeUrl?: boolean;
};

type ActionRoutesConfig<E extends StringEnum> = {
  [K in E[keyof E]]: ActionRouteConfig;
};

export enum EventType {
  First = 'first',
  Second = 'second',
  Third = 'third',
}

export const ALREADY_SPINED_SLOT_STR = 'already_spined_slot';

const DISABLE_PROMO_TEMPLATE_SLUG = 'disable-promo-template';

export abstract class BasePromo<IBasePromo = unknown> {
  /**
   * Abstract event name for hunt(halloween, easter-eggs ...)
   */
  public abstract eventName: string;

  /**
   * Event type elements
   */
  public eventType = EventType;

  /**
   * Event collected list
   */
  public eventList: unknown[] = null;

  /**
   * Event Loaded
   */
  private _eventLoaded = null;

  public huntModalData: any = {};

  public isDisabledPromo: boolean;

  private _platform: PlatformService = inject(PlatformService);
  private _static: StaticContentService = inject(StaticContentService);
  private _events: GlobalEventsService = inject(GlobalEventsService);
  private _window = inject<Window>(WINDOW);
  public cookie: CookieService = inject(CookieService);
  public group: UserGroupsService = inject(UserGroupsService);
  public bonuses: UserBonusesService = inject(UserBonusesService);
  public user: UserService = inject(UserService);
  public paymentMode: UserPaymentModeService = inject(UserPaymentModeService);
  public page: StaticPageService = inject(StaticPageService);
  public lootboxService: LootboxService = inject(LootboxService);
  public time: TimeService = inject(TimeService);
  public tournaments: TournamentsService = inject(TournamentsService);
  public local: LocalHistoryService = inject(LocalHistoryService);
  public router: Router = inject(Router);
  public route: ActivatedRoute = inject(ActivatedRoute);
  public translate: TranslationService = inject(TranslationService);
  public window: WindowService = inject(WindowService);
  public platform: PlatformService = inject(PlatformService);
  public modals: ModalService = inject(ModalService);
  public localStorage: LocalstorageService = inject(LocalstorageService);
  public responseHandler: ResponseHandlerService = inject(ResponseHandlerService);

  constructor() {
  }

  public get eventEnable(): boolean {
    return this._eventLoaded;
  }

  public get isFirstExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.First) && this.user.auth;
  }

  public get isSecondExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.Second) && this.user.auth;
  }

  public get isThirdExist(): boolean {
    return this._eventLoaded && !this.eventList.includes(this.eventType.Third) && this.user.auth;
  }

  /**
   * Fetch necessary data for promo
   * @public
   */
  public fetchPromoData$() {
    return this.paymentMode.currentMode$.pipe(
      switchMap((mode) => {
        const preparedSlug = mode ? `${this.eventName}-${mode}-${new Date().getFullYear() % 100}` : this.eventName;
        return combineLatest(
          [
            this.page.item({ slug: preparedSlug }).pipe(
              filter(page => !!page),
              map(page => page?.map(item =>
                ({ ...item, isDisabledPromo: item?.template_slug === DISABLE_PROMO_TEMPLATE_SLUG }))),
            ),
            ...this.loadNecessaryBonuses$(),
          ],
        );
      }),
    );
  }

  public readQueryParamsDepositSuccess() {
    this.route.queryParams.pipe(
      filter(({ deposit_success }) => !!deposit_success),
      tap(() => this.cookie.delete(ALREADY_SPINED_SLOT_STR)),
    ).subscribe();
  }

  public loadNecessaryBonuses$() {
    return [
      this.user.auth ? this.bonuses.lootBoxList() : of([]),
      this.user.auth ? this.bonuses.freeSpinsList() : of([]),
      this.user.auth ? this.bonuses.bonusList() : of([]),
    ];
  }

  public getFilteredCashWelcomeBonuses(arrayToCheck: any[]): any {
    return arrayToCheck
      ?.filter(bonus => bonus.stage === BonusStage.HANDLE_BETS && bonus.isWelcome && !this.cookie.check(ALREADY_SPINED_SLOT_STR))
      .sort((a, b) => b.created_at - a.created_at)[0];
  }

  public getFilteredFreespinsWelcomeBonuses(arrayToCheck: any[]): any {
    return arrayToCheck
      ?.filter(bonus => bonus.stage === BonusStage.ISSUED && bonus.isWelcome && !this.cookie.check(ALREADY_SPINED_SLOT_STR))
      .sort((a, b) => b.created_at - a.created_at)[0];
  }

  public getFilteredWelcomeLootbox(arrayToCheck: any[]): any {
    return arrayToCheck?.filter(lootbox => lootbox.isWelcome && lootbox.stage === BonusStage.ISSUED)[0];
  }

  public checkActivatedTodayLootbox(promoLootbox: any, ids: string[]): boolean {
    return (
      promoLootbox?.stage === BonusStage.ACTIVATED && ids.includes(promoLootbox.e.title) && this.time.isTodayDate(promoLootbox?.created_at)
    );
  }

  public getFilteredPromoLootbox(arrayToCheck: any[], bonusIds: string[], stage: BonusStage): any {
    return arrayToCheck
      ?.filter(lootbox => bonusIds.includes(lootbox.e.title) && lootbox.e.stage === stage)
      .sort((a, b) => b.e.created_at - a.e.created_at)[0];
  }

  public updateParticipateInPromo(lootboxes: any, bonusIds: string[], storeRef: StoreEntityType<any>) {
    const promoIssuedLootbox = storeRef.storeSignal()?.currentBonus?.promoIssuedLootbox;
    const promoActivatedLootbox = this.getFilteredPromoLootbox(lootboxes, bonusIds, BonusStage.ACTIVATED);
    const cash = storeRef.storeSignal()?.currentBonus?.cash;
    const fs = storeRef.storeSignal()?.currentBonus?.fs;
    const welcomeLootbox = storeRef.storeSignal()?.currentBonus?.welcomeLootbox;

    const isUserAlreadyActivatedPromoLootbox = this.checkActivatedTodayLootbox(promoActivatedLootbox, bonusIds);
    const isAllWelcomePackClaimed = this.group.isExistGroup('ID407');
    const isUserCanParticipateInPromo = Boolean(isAllWelcomePackClaimed && promoIssuedLootbox);
    const isUserCanPartiallyParticipateInPromo = Boolean((cash || fs || welcomeLootbox) && !promoIssuedLootbox);

    storeRef.storeSignal.update(store => {
      return {
        ...store,
        isUserCanParticipateInPromo,
        isUserCanPartiallyParticipateInPromo,
        isAllWelcomePackClaimed,
        isUserAlreadyActivatedPromoLootbox,
      };
    });
  }

  public isPromoError<T>(promo: T): promo is T & { statusCode: HttpStatus } {
    return (
      typeof promo === 'object' &&
      'statusCode' in promo &&
      (promo.statusCode === HttpStatus.MOVED_PERMANENTLY || promo.statusCode === HttpStatus.NOT_FOUND)
    );
  }

  public getTournaments$(tournamentPromoSlug: string): Observable<any> {
    return this.tournaments.list({ with_game_list: 1 }).pipe(
      filter(tournaments => !!tournaments),
      map(tournaments =>
        tournaments.now.filter(tournament => {
          return tournament.slug.includes(tournamentPromoSlug);
        }),
      ),
    );
  }

  public updateCurrentBonus(lootboxes: any, freespins: any, bonuses: any, bonusIds: string[], storeRef: StoreEntityType<any>) {
    const cash = this.getFilteredCashWelcomeBonuses(bonuses);
    const fs = this.getFilteredFreespinsWelcomeBonuses(freespins);
    const welcomeLootbox = this.getFilteredWelcomeLootbox(lootboxes);
    const promoIssuedLootbox = this.getFilteredPromoLootbox(lootboxes, bonusIds, BonusStage.ISSUED);

    storeRef.storeSignal.update(store => {
      return {
        ...store,
        currentBonus:
          (fs && cash) || (welcomeLootbox && cash) || fs || promoIssuedLootbox
            ? {
              cash,
              fs,
              welcomeLootbox,
              promoIssuedLootbox,
            }
            : null,
      };
    });
  }

  /**
   * Update current prize store
   * @param fs_count
   * @param cash
   * @param storeRef
   * @private
   */
  public updateCurrentPrize(fs_count: number, cash: string, storeRef: StoreEntityType<any>) {
    storeRef.storeSignal.update(store => {
      return {
        ...store,
        currentPrize: {
          fs_count,
          cash,
        },
      };
    });
    this.cookie.set(ALREADY_SPINED_SLOT_STR, '1');
  }

  public addEventToCollection(type: EventType) {
    if (this._platform.isBrowser) {
      this.eventList.push(type);
      this.cookie.set(this.eventName, JSON.stringify(this.eventList), 999, '/');
      if (this.eventList.length === 3) {
        this.group.addToGroup(this.huntModalData?.HuntModal?.BonusGroupCatch)?.subscribe();
      }
      this.openHuntModal(type);
    }
  }

  public initHuntEvent(HuntModal: any) {
    this.huntModalData = HuntModal;
    if (!this.group.isExistGroup(this.huntModalData?.HuntModal?.BonusGroupClaimed)) {
      if (this.group.isExistGroup(this.huntModalData?.HuntModal?.BonusGroupCatch)) {
        this.eventList = [this.eventType.First, this.eventType.Second, this.eventType.Third];
      } else {
        this.eventList = this.cookie.check(this.eventName) ? JSON.parse(this.cookie.get(this.eventName)) : [];
      }
      this._eventLoaded = true;
    } else {
      this._eventLoaded = false;
    }
  }

  public initWelcomePromoModal() {
    return this._checkShouldOpenWelcomeModal$();
  }

  public async onAction<E extends StringEnum>(
    actionEnum: E,
    action: E[keyof E],
    configMap: ActionRoutesConfig<E>,
    bonusCode?: string,
    successUrl: string = this.window.nativeWindow.location.href + '?deposit_success=true',
  ) {
    const routeConfig = configMap[action];
    const { key, url, authUrl } = routeConfig;

    if (action === actionEnum.GO_TO_DEPOSIT) {
      await this._handleActionRoute(key, url, authUrl, { bonusCode }, { queryParams: { successUrl, bonusCode } });
    } else if (action === actionEnum.HUNT_DEPOSIT) {
      const promoUrl = `${this.window.nativeWindow.location.origin}/promo/${this.eventName}?deposit_success=true`;
      await this._handleActionRoute(key, url, authUrl, { bonusCode }, {
        queryParams: {
          successUrl: promoUrl,
          bonusCode,
        },
      });
    } else if (configMap[action]?.nativeUrl) {
      this.window.nativeWindow.open(configMap[action].url, '_blank');
    } else {
      await this._handleActionRoute(key, url, authUrl);
    }
  }

  /**
   * Activate bonus by type of bonus
   */
  public onActivateBonus$(storeRef: StoreEntityType<any>): Observable<any> {
    const store = storeRef.storeSignal();
    const { promoIssuedLootbox, welcomeLootbox, fs, cash } = store.currentBonus || {};

    const cashPrize = cash ? `${cash.amount} ${cash.currency}` : undefined;

    if (store.isUserCanPartiallyParticipateInPromo) {
      if (welcomeLootbox) {
        return this.lootboxService.activateLootBox(welcomeLootbox.id).pipe(
          switchMap(response => {
            const fs_count = this.bonuses.resolveBonusAttributes(response?.bonuses[0].attributes)?.freespins_count;
            this.updateCurrentPrize(fs_count, cashPrize ?? undefined, storeRef);
            const currentFsBonus = this.bonuses.mapBonusTitle(response?.bonuses[0]?.title);
            return this.bonuses.freeSpinsList().pipe(
              switchMap(list => {
                const foundFs = list.find(e => e.title === currentFsBonus);
                return this.bonuses.activateFreeSpins(foundFs?.id);
              }),
            );
          }),
        );
      } else if (fs) {
        return this.bonuses.activateFreeSpins(fs?.id).pipe(
          tap(() => {
            this.updateCurrentPrize(fs?.freespins_total, cashPrize ?? undefined, storeRef);
          }),
        );
      }
    }

    if (promoIssuedLootbox) {
      return this.lootboxService.activateLootBox(promoIssuedLootbox?.id).pipe(
        switchMap(response => {
          const fs_count = this.bonuses.resolveBonusAttributes(response?.bonuses[0].attributes)?.freespins_count;
          this.updateCurrentPrize(fs_count, '', storeRef);
          const currentFsBonus = this.bonuses.mapBonusTitle(response?.bonuses[0]?.title);
          return this.bonuses.freeSpinsList().pipe(
            switchMap(list => {
              const foundFs = list.find(e => e.title === currentFsBonus);
              return this.bonuses.activateFreeSpins(foundFs?.id);
            }),
          );
        }),
      );
    }
  }

  private async _handleActionRoute(
    key: string,
    url: string,
    authUrl: string[],
    urlData?: Record<string, any>,
    extras?: NavigationExtras,
  ): Promise<void> {
    if (!key) {
      await this.router.navigateByUrl(url);
      return;
    }

    if (!this.user.auth) {
      this.local.addUrl(key, url, urlData);
      await this.user.authUser();
      return;
    }

    await this.router.navigate(authUrl, extras);
  }

  private _checkShouldOpenWelcomeModal$(): Observable<boolean> {
    const routesIgnore = ['403', '404', 'register', 'login', 'deposit', 'cashout', 'games', 'promo', 'play'];
    return this._events.routerNavigationEnd$.pipe(
      takeWhile(() => !this.cookie.check('welcome-modal-promo'), true),
      distinctUntilChanged(),
      delay(120000),
      filter(() => {
        const hasWelcomeModalCookie = this.cookie.check('welcome-modal-promo');
        const shouldOpenModal = this.user.auth && !hasWelcomeModalCookie;
        return shouldOpenModal && !routesIgnore.some(route => this.router.url.includes(route));
      }),
      switchMap(() => this.checkActivatedBonuses()),
    );
  }

  public abstract openHuntModal?(type: EventType): void;

  public abstract checkActivatedBonuses?(): Observable<any> | Promise<any>;
}
